/**
 * AppShell — authenticated layout: sidebar + main content area (issue #7).
 *
 * Wraps role-gated feature pages. Provides:
 *   - SidebarProvider (collapsible state management)
 *   - AppSidebar with role-resolved nav + tenant branding from GET /tenant
 *   - SidebarInset for the main content area, under the visit host (O10)
 *
 * Tenant branding comes from the presentation hook, including explicit loading
 * and error fallback policy.
 *
 * Usage in App.tsx:
 *   <ProtectedRoute roles={["admin"]}>
 *     <AppShell><AdminDashboard /></AppShell>
 *   </ProtectedRoute>
 */
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";

import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { VisitHost } from "@/components/visit/VisitHost";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantPresentation } from "@/hooks/use-tenant-presentation";
import { announcementsApi } from "@/lib/api/announcements";
import { frameHook } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { PROTECTED_ROUTES } from "@/navigation/routes";
import { isRegistrarWorkspacePath, sidebarItemsForRole } from "@/navigation/sidebar-items";
import { AppSidebar } from "./AppSidebar";
import { InboxLink } from "./InboxLink";

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { me } = useAuth();
  const location = useLocation();
  const { name: tenantName, sidebarLogoUrl, parentOrg } = useTenantPresentation();

  const items = sidebarItemsForRole(me?.role ?? null, me?.workspaces);
  const roleLabel = location.pathname.startsWith("/student")
    ? "My Learning"
    : location.pathname.startsWith("/admin/teaching")
      ? "My Teaching"
      : me?.role === "admin" && isRegistrarWorkspacePath(location.pathname)
        ? "Registrar"
        : me?.role ? `${me.role[0].toUpperCase()}${me.role.slice(1)} Portal` : "Member Portal";
  const routeFamily = location.pathname.startsWith("/student")
    ? "student"
    : location.pathname.startsWith("/admin/teaching")
      ? "adminTeaching"
      : location.pathname.startsWith("/instructor")
        ? "instructor"
        : me?.role;
  const inboxUrl = routeFamily ? `${PROTECTED_ROUTES[routeFamily].inbox}${routeFamily === "admin" ? "" : "?view=notices"}` : undefined;
  const headerHref = routeFamily ? PROTECTED_ROUTES[routeFamily].dashboard : undefined;
  const tenantId = me?.tenant_id;
  const userId = me?.user_id;
  const unreadQuery = useQuery({
    queryKey: [
      "announcements",
      "unread-count",
      "academy",
      tenantId ?? "unresolved",
      userId ?? "unresolved",
    ],
    queryFn: () => announcementsApi.unreadCount("academy"),
    enabled: Boolean(inboxUrl && tenantId && userId),
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 1,
  });
  const unreadCount = unreadQuery.data?.unread_count ?? 0;
  const inboxLabel = unreadCount > 0
    ? `Open academy notices, ${unreadCount} unread institution ${unreadCount === 1 ? "announcement" : "announcements"}`
    : "Open academy notices";
  const today = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date());

  return (
    <SidebarProvider>
      <AppSidebar
        items={items}
        tenantName={tenantName}
        logoUrl={sidebarLogoUrl}
        parentOrg={parentOrg}
        headerHref={headerHref}
        contextLabel={roleLabel}
        inbox={inboxUrl ? { href: inboxUrl, label: inboxLabel, unread: unreadCount > 0 } : undefined}
      />
      <SidebarInset className="min-w-0 bg-background">
        {/* Mobile only: on desktop the rail header carries context and inbox.
            The bar stands in for that header on the same rhythm: the workspace
            line, one meta line, and the inbox as the one control it shares. */}
        <header className="app-topbar md:hidden" {...frameHook("rail")}>
          <SidebarTrigger className="-ms-1" />
          <div className="min-w-0">
            <div className="truncate text-ui font-medium text-foreground">{roleLabel}</div>
            <div className="truncate text-small text-muted-foreground">{tenantName}</div>
          </div>
          <div className="ms-auto flex items-center gap-2">
            <time className="hidden text-small text-muted-foreground sm:block" dateTime={new Date().toISOString()}>
              {today}
            </time>
            {inboxUrl && (
              <InboxLink
                href={inboxUrl}
                label={inboxLabel}
                unread={unreadCount > 0}
                count={unreadCount}
                className="size-[var(--control-height)] hover:bg-well"
              />
            )}
          </div>
        </header>
        {/* Every Bento page here can visit a box in place (O10). */}
        <div className="min-w-0 flex-1">
          <VisitHost>{children}</VisitHost>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
